/**
 * Created by christiancueni on 26/09/16.
 * Copyright (c) 2016 ITerativ GmbH. All rights reserved.
 */
import * as dayjs from 'dayjs';
import {CustomAction} from '../actions/CustomAction';
import {type} from "../shared/util";
import {BackendEvent, FormEvent, ViewEvent} from "../event/event";
import {Comment} from '../models/comment';
import {AdminMessage} from "../admin-messages/admin-message";
import {AttachmentRequestParameters, AttachmentResponse} from "../attachment/attachment.model";
import {DocumentOrderFilterType} from "../my-documents/document-order-filter";

export const KlassenbuchActionTypes = {
  UPDATE_WEEKDAYS: type("[Klassenbuch] Update weekdays"),
  DATE_SELECTED: type("[Klassenbuch] Date selected"),
  TODAY_SELECTED: type("[Klassenbuch] Today selected"),
  ADD_EVENT: type("[Klassenbuch] Add event"),
  ADD_EVENT_SUCCESS: type("[Klassenbuch] Add event success"),
  ADD_EVENT_FAIL: type("[Klassenbuch] Add event fail"),
  GET_EVENTS: type("[Klassenbuch] Get events"),
  GET_EVENTS_SUCCESS: type("[Klassenbuch] Get events success"),
  GET_EVENTS_FAIL: type("[Klassenbuch] Get events fail"),
  SHOW_PREV_EVENT: type("[Klassenbuch] Show previous event"),
  SHOW_NEXT_EVENT: type("[Klassenbuch] Show next event"),
  SET_SELECT_DISPLAY_GROUPS: type("[Klassenbuch] Set select display group"),
  ADD_SELECT_DISPLAY_GROUP: type("[Klassenbuch] Add select display group"),
  REMOVE_SELECT_DISPLAY_GROUP: type("[Klassenbuch] Remove select display group"),
  PUBLISH_EVENT: type("[Klassenbuch] Publish Event"),
  PUBLISH_UPDATE_EVENT: type("[Klassenbuch] Publish-Update Event"),
  PUBLISH_UPDATE_EVENT_SUCCESS: type("[Klassenbuch] Publish-Update event success"),
  UPDATE_EVENT: type("[Klassenbuch] Update Event"),
  UPDATE_EVENT_SUCCESS: type("[Klassenbuch] Update event success"),
  UPDATE_EVENT_FAIL: type("[Klassenbuch] Update event fail"),
  GET_EVENT: type("[Klassenbuch] Get Event"),
  GET_EVENT_SUCCESS: type("[Klassenbuch] Get event success"),
  GET_EVENT_FAIL: type("[Klassenbuch] Get event fail"),
  ADD_COMMENT: type("[Klassenbuch] Add comment"),
  ADD_COMMENT_SUCCESS: type("[Klassenbuch] Add comment success"),
  ADD_COMMENT_FAIL: type("[Klassenbuch] Add comment fail"),
  DELETE_COMMENT: type("[Klassenbuch] Delete comment"),
  DELETE_COMMENT_SUCCESS: type("[Klassenbuch] Delete comment success"),
  DELETE_COMMENT_FAIL: type("[Klassenbuch] Delete comment fail"),
  LOAD_COMMENTS: type("[Klassenbuch] Load comments"),
  LOAD_COMMENTS_SUCCESS: type("[Klassenbuch] Load comments success"),
  LOAD_COMMENTS_FAIL: type("[Klassenbuch] Load comments fail"),
  DELETE_EVENT_USER_ACTION: type("[Klassenbuch] Delete event user action"),
  DELETE_EVENT_CONFIRMATION: type("[Klassenbuch] Delete event confirmation"),
  DELETE_EVENT_CANCEL: type("[Klassenbuch] Delete event cancel"),
  DELETE_EVENT: type("[Klassenbuch] Delete event"),
  DELETE_EVENT_SUCCESS: type("[Klassenbuch] Delete event success"),
  DELETE_EVENT_FAIL: type("[Klassenbuch] Delete event fail"),
  RESET_DETAIL_EVENT: type("[Klassenbuch] Reset Event"),
  GET_MY_DOCUMENTS: type("[Klassenbuch] Get my documents event"),
  GET_MY_DOCUMENTS_SUCCESS: type("[Klassenbuch] Get my documents event success"),
  GET_MY_DOCUMENTS_FAIL: type("[Klassenbuch] Get my documents event failure"),
  DELETE_DOCUMENTS: type("[Klassenbuch] Deletes document"),
  DELETE_DOCUMENTS_SUCCESS: type("[Klassenbuch] Deletes document success"),
  UPDATE_DOCUMENT_FILTER: type("[Klassenbuch] Update document filter"),
  UPDATE_DOCUMENT_PAGE: type("[Klassenbuch] Update document page"),
  UPDATE_DOCUMENT_QUERY: type("[Klassenbuch] Update document query"),
  PRERENDER_EVENT: type("[Klassenbuch] Prerender Event"),
  GET_EVENT_FROM_STORAGE_SUCCESS: type('[Klassenbuch] Get event from storage success'),
  GET_EVENTS_FROM_STORAGE_SUCCESS: type('[Klassenbuch] Get events from storage success'),
  PREFETCH_NEXT_WEEKS: type('[Klassenbuch] Prefetch Next Week'),
  LOAD_ADMIN_MESSAGES: type("[Klassenbuch] Load admin messages"),
  LOAD_ADMIN_MESSAGES_SUCCESS: type("[Klassenbuch] Load admin messages success"),
  LOAD_ADMIN_MESSAGES_FAILURE: type("[Klassenbuch] Load admin messages failure"),
  MARK_ADMIN_MESSAGE_READ: type("[Klassenbuch] Mark admin message read"),
  MARK_ADMIN_MESSAGE_READ_SUCCESS: type("[Klassenbuch] Mark admin message read success"),
  MARK_ADMIN_MESSAGE_READ_FAILURE: type("[Klassenbuch] Mark admin message read failure"),
  RESET_KLASSENBUCH: type("[Klassenbuch] Reset klassenbuch")
};

export class UpdateWeekdaysAction implements CustomAction {
  type = KlassenbuchActionTypes.UPDATE_WEEKDAYS;

  constructor(public payload: { selectedDate: dayjs.Dayjs, weekdays: string[] }) {
  }
}

export class DateSelectedAction implements CustomAction {
  type = KlassenbuchActionTypes.DATE_SELECTED;

  constructor(public payload: { date: string, sameWeek: boolean }) {
  }
}

export class TodaySelectedAction implements CustomAction {
  type = KlassenbuchActionTypes.TODAY_SELECTED;

  constructor() {
  }
}

export class AddEventAction implements CustomAction {
  type = KlassenbuchActionTypes.ADD_EVENT;

  constructor(public payload: FormEvent) {
  }
}

export class AddEventSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.ADD_EVENT_SUCCESS;

  constructor(public payload: Object) {
  }
}

export class AddEventFailAction implements CustomAction {
  type = KlassenbuchActionTypes.ADD_EVENT_FAIL;

  constructor(public payload: string) {
  }
}

export class GetEventsAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_EVENTS;

  constructor(public payload: { groups: Object[], start: string, end: string }) {
  }
}

export class GetEventsSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_EVENTS_SUCCESS;

  constructor(public payload: BackendEvent[]) {
  }
}

export class GetEventsFailAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_EVENTS_FAIL;

  constructor(public payload: string) {
  }
}

export class ShowPreviousEventAction implements CustomAction {
  type = KlassenbuchActionTypes.SHOW_PREV_EVENT;

  constructor(public payload: { x: number, y: number }) {
  }
}

export class ShowNextEventAction implements CustomAction {
  type = KlassenbuchActionTypes.SHOW_NEXT_EVENT;

  constructor(public payload: { x: number, y: number }) {
  }
}

export class SetSelectDisplayGroupAction implements CustomAction {
  type = KlassenbuchActionTypes.SET_SELECT_DISPLAY_GROUPS;

  constructor(public payload: number[]) {
  }
}

export class AddSelectDisplayGroupAction implements CustomAction {
  type = KlassenbuchActionTypes.ADD_SELECT_DISPLAY_GROUP;

  constructor(public payload: number) {
  }
}

export class RemoveSelectDisplayGroupAction implements CustomAction {
  type = KlassenbuchActionTypes.REMOVE_SELECT_DISPLAY_GROUP;

  constructor(public payload: number) {
  }
}

export class PublishEventAction implements CustomAction {
  type = KlassenbuchActionTypes.PUBLISH_EVENT;

  constructor(public payload: Object) {
  }
}

export class PublishUpdateEventAction implements CustomAction {
  type = KlassenbuchActionTypes.PUBLISH_UPDATE_EVENT;

  constructor(public payload: Object) {
  }
}

export class PublishUpdateEventSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.PUBLISH_UPDATE_EVENT_SUCCESS;

  constructor(public payload: Object) {
  }
}

export class UpdateEventAction implements CustomAction {
  type = KlassenbuchActionTypes.UPDATE_EVENT;

  constructor(public payload: Object) {
  }
}

export class UpdateEventSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.UPDATE_EVENT_SUCCESS;

  constructor(public payload: Object) {
  }
}

export class UpdateEventFailAction implements CustomAction {
  type = KlassenbuchActionTypes.UPDATE_EVENT_FAIL;

  constructor(public payload: Object) {
  }
}

export class GetEventAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_EVENT;

  constructor(public payload: string) {
  }
}

export class GetEventSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_EVENT_SUCCESS;

  constructor(public payload: Object) {
  }
}

export class GetEventFailAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_EVENT_FAIL;

  constructor(public payload: Object) {
  }
}

export class AddCommentAction implements CustomAction {
  type = KlassenbuchActionTypes.ADD_COMMENT;

  constructor(public payload: Comment) {
  }
}

export class AddCommentSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.ADD_COMMENT_SUCCESS;

  constructor(public payload: Object) {
  }
}

export class AddCommentFailAction implements CustomAction {
  type = KlassenbuchActionTypes.ADD_COMMENT_FAIL;

  constructor(public payload: Object) {
  }
}

export class DeleteCommentAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_COMMENT;

  constructor(public payload: Comment) {
  }
}

export class DeleteCommentSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_COMMENT_SUCCESS;

  constructor(public payload: Object) {
  }
}

export class DeleteCommentFailAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_COMMENT_FAIL;

  constructor(public payload: Object) {
  }
}

export class LoadCommentsAction implements CustomAction {
  type = KlassenbuchActionTypes.LOAD_COMMENTS;

  constructor(public payload: Object) {
  }
}

export class LoadCommentsSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.LOAD_COMMENTS_SUCCESS;

  constructor(public payload: Object) {
  }
}

export class LoadCommentsFailAction implements CustomAction {
  type = KlassenbuchActionTypes.LOAD_COMMENTS_SUCCESS;

  constructor(public payload: Object) {
  }
}

export class DeleteEventUserAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_EVENT_USER_ACTION;

  constructor(public payload: ViewEvent) {
  }
}

export class DeleteEventConfirmationAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_EVENT_CONFIRMATION;

  constructor(public payload: { event: ViewEvent, singleDelete: boolean }) {
  }
}

export class DeleteEventCancelAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_EVENT_CANCEL;

  constructor() {
  }
}

export class DeleteEventAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_EVENT;

  constructor(public payload: { event: ViewEvent, singleDelete: boolean }) {
  }
}

export class DeleteEventSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_EVENT_SUCCESS;

  constructor() {
  }
}

export class DeleteEventFailAction implements CustomAction {
  type = KlassenbuchActionTypes.LOAD_COMMENTS_FAIL;

  constructor(public payload: Object) {
  }
}

export class ResetEventAction implements CustomAction {
  type = KlassenbuchActionTypes.RESET_DETAIL_EVENT;

  constructor() {
  }
}

export class GetMyDocumentsAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_MY_DOCUMENTS;

  constructor(public payload: AttachmentRequestParameters) {
  }
}

export class GetMyDocumentsSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_MY_DOCUMENTS_SUCCESS;

  constructor(public payload: AttachmentResponse) {
  }
}

export class GetMyDocumentsFailAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_MY_DOCUMENTS_FAIL;

  constructor() {
  }
}

export class DeleteDocumentsAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_DOCUMENTS;

  constructor(public payload: number[]) {
  }
}

export class DeleteDocumentsSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.DELETE_DOCUMENTS_SUCCESS;

  constructor(public payload: number[]) {
  }
}

export class UpdateDocumentOrderFilterAction implements CustomAction {
  type = KlassenbuchActionTypes.UPDATE_DOCUMENT_FILTER;

  constructor(public payload: DocumentOrderFilterType) {

  }
}

export class UpdateDocumentPageAction implements CustomAction {
  type = KlassenbuchActionTypes.UPDATE_DOCUMENT_PAGE

  constructor(public payload: number) {

  }
}

export class UpdateDocumentQueryAction implements CustomAction {
  type = KlassenbuchActionTypes.UPDATE_DOCUMENT_QUERY

  constructor(public payload: string) {

  }
}

export class PrerenderEventAction implements CustomAction {
  type = KlassenbuchActionTypes.PRERENDER_EVENT;

  constructor(public payload: ViewEvent) {
  }
}

export class LoadAdminMessagesAction implements CustomAction {
  type = KlassenbuchActionTypes.LOAD_ADMIN_MESSAGES;

  constructor() {
  }
}

export class LoadAdminMessagesFailureAction implements CustomAction {
  type = KlassenbuchActionTypes.LOAD_ADMIN_MESSAGES_FAILURE;

  constructor() {
  }
}

export class LoadAdminMessagesSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.LOAD_ADMIN_MESSAGES_SUCCESS;

  constructor(public payload: AdminMessage[]) {
  }
}

export class MarkAdminMessageReadAction implements CustomAction {
  type = KlassenbuchActionTypes.MARK_ADMIN_MESSAGE_READ;

  constructor(public payload: number) {
  }
}

export class MarkAdminMessageReadSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.MARK_ADMIN_MESSAGE_READ_SUCCESS;

  constructor() {
  }
}

export class MarkAdminMessageReadFailureAction implements CustomAction {
  type = KlassenbuchActionTypes.MARK_ADMIN_MESSAGE_READ_FAILURE;

  constructor() {
  }
}

export class GetEventFromStorageSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_EVENT_FROM_STORAGE_SUCCESS;

  constructor(public payload: ViewEvent) {
  }
}

export class GetEventsFromStorageSuccessAction implements CustomAction {
  type = KlassenbuchActionTypes.GET_EVENTS_FROM_STORAGE_SUCCESS;

  constructor(public payload: ViewEvent[][]) {
  }
}

export class PrefetchNextWeeksAction implements CustomAction {
  type = KlassenbuchActionTypes.PREFETCH_NEXT_WEEKS;

  constructor() {
  }
}

export class ResetKlassenbuchAction implements CustomAction {
  type = KlassenbuchActionTypes.RESET_KLASSENBUCH;

  constructor() {
  }
}

export type KlassenbuchActions =
  UpdateWeekdaysAction
  | DateSelectedAction
  | TodaySelectedAction
  | AddEventAction
  | AddEventSuccessAction
  | AddEventFailAction
  | GetEventsAction
  | GetEventsSuccessAction
  | GetEventsFailAction
  | ShowPreviousEventAction
  | ShowNextEventAction
  | SetSelectDisplayGroupAction
  | AddSelectDisplayGroupAction
  | RemoveSelectDisplayGroupAction
  | PublishEventAction
  | PublishUpdateEventAction
  | PublishUpdateEventSuccessAction
  | UpdateEventAction
  | UpdateEventSuccessAction
  | UpdateEventFailAction
  | GetEventAction
  | GetEventSuccessAction
  | GetEventFailAction
  | AddCommentAction
  | AddCommentSuccessAction
  | AddCommentFailAction
  | DeleteCommentAction
  | DeleteCommentSuccessAction
  | DeleteCommentFailAction
  | LoadCommentsAction
  | LoadCommentsSuccessAction
  | LoadCommentsFailAction
  | DeleteEventUserAction
  | DeleteEventCancelAction
  | DeleteEventConfirmationAction
  | DeleteEventAction
  | DeleteEventSuccessAction
  | DeleteEventFailAction
  | ResetEventAction
  | GetMyDocumentsAction
  | GetMyDocumentsSuccessAction
  | GetMyDocumentsFailAction
  | UpdateDocumentOrderFilterAction
  | UpdateDocumentPageAction
  | UpdateDocumentQueryAction
  | DeleteDocumentsAction
  | DeleteDocumentsSuccessAction
  | PrerenderEventAction
  | GetEventFromStorageSuccessAction
  | GetEventsFromStorageSuccessAction
  | PrefetchNextWeeksAction
  | LoadAdminMessagesAction
  | LoadAdminMessagesFailureAction
  | LoadAdminMessagesSuccessAction
  | MarkAdminMessageReadAction
  | MarkAdminMessageReadFailureAction
  | MarkAdminMessageReadSuccessAction
  | ResetKlassenbuchAction;
